import * as THREE from 'three';
import type { SurfaceEffect } from '@vrlearning/lesson-schema';
import { fbm, rippleNormals } from '../../look/noise.js';
import { caustics } from '../patterns.js';
import type { WorldBuilder } from '../world.js';

/**
 * The headset's own shaders for a package's meshes (PR 0 decision 4): the island's lava, the sea
 * floor's caustics and the water's surface seen from below. Moved from volcano.ts and seafloor.ts,
 * and kept when the code worlds went (12 Oct 2026). Each has a `uTime` clock.
 * The lava reads the mesh's UVs; the other two read where the mesh stands.
 */

const lavaVertex = /* glsl */ `
#include <common>
#include <fog_pars_vertex>
varying vec2 vUv;
void main() {
  vUv = uv;
  vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;

const lavaFragment = /* glsl */ `
#include <common>
#include <fog_pars_fragment>
uniform float uTime;
uniform sampler2D uNoise;
varying vec2 vUv;
void main() {
  vec2 p = vUv * 3.0;
  float a = texture2D(uNoise, p + vec2(uTime * 0.012, uTime * 0.007)).r;
  float b = texture2D(uNoise, p * 2.1 - vec2(uTime * 0.009, -uTime * 0.013)).r;
  float heat = smoothstep(0.42, 0.75, a * 0.65 + b * 0.55);
  // A dark crust broken by glowing seams, hottest in the middle.
  float centre = 1.0 - smoothstep(0.1, 0.5, length(vUv - 0.5));
  vec3 crust = vec3(0.09, 0.025, 0.012);
  vec3 hot = mix(vec3(1.1, 0.26, 0.04), vec3(1.5, 0.75, 0.2), heat * heat);
  gl_FragColor = vec4(mix(crust, hot, clamp(heat * 0.85 + centre * 0.35, 0.0, 1.0)), 1.0);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

/** A tiling scalar noise, for the lava's crust. */
function lavaNoise(size = 64): THREE.DataTexture {
  const noise = fbm(19, 4, 8);
  const data = new Uint8Array(size * size * 4);
  for (let y = 0; y < size; y++) {
    for (let x = 0; x < size; x++) {
      const v = Math.round(noise((x / size) * 8, (y / size) * 8) * 255);
      data.set([v, v, v, 255], (y * size + x) * 4);
    }
  }
  const texture = new THREE.DataTexture(data, size, size);
  texture.wrapS = THREE.RepeatWrapping;
  texture.wrapT = THREE.RepeatWrapping;
  texture.magFilter = THREE.LinearFilter;
  texture.minFilter = THREE.LinearFilter;
  texture.needsUpdate = true;
  return texture;
}

const causticsVertex = /* glsl */ `
#include <common>
#include <fog_pars_vertex>
varying vec2 vWorld;
void main() {
  vec4 world = modelMatrix * vec4(position, 1.0);
  vWorld = world.xz;
  vec4 mvPosition = viewMatrix * world;
  gl_Position = projectionMatrix * mvPosition;
  #include <fog_vertex>
}
`;
const causticsFragment = /* glsl */ `
#include <common>
#include <fog_pars_fragment>
uniform sampler2D uNet;
uniform float uTime;
uniform vec3 uLight;
varying vec2 vWorld;
void main() {
  float a = texture2D(uNet, vWorld * 0.21 + vec2(uTime * 0.017, uTime * 0.011)).r;
  float b = texture2D(uNet, vWorld * 0.17 - vec2(uTime * 0.013, -uTime * 0.019)).r;
  float light = min(a, b) * 1.6;
  float fade = 1.0 - smoothstep(15.0, 25.0, length(vWorld));
  gl_FragColor = vec4(uLight, clamp(light * fade, 0.0, 1.0) * 0.55);
  #include <tonemapping_fragment>
  #include <colorspace_fragment>
  #include <fog_fragment>
}
`;

/** The lava's material: a dark crust broken by glowing seams, drifting; the mesh needs UVs. */
export function lavaMaterial(): THREE.ShaderMaterial {
  const material = new THREE.ShaderMaterial({
    vertexShader: lavaVertex,
    fragmentShader: lavaFragment,
    fog: true,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      { uTime: { value: 0 }, uNoise: { value: null } },
    ]),
  });
  material.uniforms['uNoise']!.value = lavaNoise();
  return material;
}

/** Caustics laid on the ground: light playing on the sand, fading out 15–25 m from the stage. */
export function causticsMaterialOf(world: WorldBuilder): THREE.ShaderMaterial {
  const net = world.codeTexture(caustics(128, 31));
  const material = new THREE.ShaderMaterial({
    vertexShader: causticsVertex,
    fragmentShader: causticsFragment,
    transparent: true,
    depthWrite: false,
    fog: true,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      { uTime: { value: 0 }, uLight: { value: new THREE.Color(0xdff8ff) }, uNet: { value: null } },
    ]),
  });
  material.uniforms['uNet']!.value = net;
  return material;
}

/** The water's surface seen from below: bright ripples fading into the water's colour. */
export function seaSurfaceMaterial(): THREE.ShaderMaterial {
  const material = new THREE.ShaderMaterial({
    vertexShader: causticsVertex,
    fragmentShader: /* glsl */ `
      #include <common>
      #include <fog_pars_fragment>
      uniform sampler2D uRipples;
      uniform float uTime;
      varying vec2 vWorld;
      void main() {
        vec3 n = texture2D(uRipples, vWorld * 0.08 + vec2(uTime * 0.01, uTime * 0.006)).xyz * 2.0 - 1.0;
        vec3 m = texture2D(uRipples, vWorld * 0.05 - vec2(uTime * 0.008, -uTime * 0.012)).xyz * 2.0 - 1.0;
        float sparkle = smoothstep(0.3, 0.9, abs(n.x + m.y));
        vec3 colour = mix(vec3(0.45, 0.78, 0.85), vec3(0.95, 1.0, 1.0), sparkle);
        gl_FragColor = vec4(colour, 1.0);
        #include <tonemapping_fragment>
        #include <colorspace_fragment>
        #include <fog_fragment>
      }
    `,
    fog: true,
    uniforms: THREE.UniformsUtils.merge([
      THREE.UniformsLib.fog,
      { uTime: { value: 0 }, uRipples: { value: null } },
    ]),
  });
  material.uniforms['uRipples']!.value = rippleNormals(128, 9);
  return material;
}

/** One of the headset's own shaders for a package's meshes, and its clock. */
export function effectMaterial(
  world: WorldBuilder,
  effect: SurfaceEffect,
): { material: THREE.ShaderMaterial; update(elapsed: number): void } {
  const material =
    effect === 'lava'
      ? lavaMaterial()
      : effect === 'caustics'
        ? causticsMaterialOf(world)
        : seaSurfaceMaterial();
  return {
    material,
    update: (elapsed) => {
      material.uniforms['uTime']!.value = elapsed;
    },
  };
}
